import React from 'react';

// Sensor Tech Terms and Conditions of Service, Service Level Agreement, and
// Temperature Monitoring Services Addendum. Content provided by Sensor Tech —
// keep the wording verbatim; edits to legal text should come from the company.
// The Order Form / Statement of Work is intentionally not included here: it is
// a fillable sales contract, not browsing terms.

function TermsOfService({ onClose }) {
  return (
    <div className="tos-overlay" onClick={onClose}>
      <div className="tos-modal" onClick={(e) => e.stopPropagation()}>
        <div className="tos-header">
          <h2>Terms and Conditions of Service</h2>
          <button className="tos-close" onClick={onClose}>&#10005;</button>
        </div>

        <div className="tos-body">
          <h3>SENSOR TECH TERMS AND CONDITIONS OF SERVICE</h3>
          <p className="tos-updated">Term dates: ____________ &ndash; Present</p>

          <p>
            These Terms and Conditions of Service describe the terms and conditions under which
            Sensor Tech, a California general partnership doing business as Sensor Tech
            (&ldquo;Sensor Tech,&rdquo; &ldquo;Company,&rdquo; &ldquo;we,&rdquo; &ldquo;us,&rdquo; or
            &ldquo;our&rdquo;), agrees to provide Client the services described herein.
          </p>
          <p>Sensor Tech operates the online dashboard and service platform located at Sensetech.app.</p>
          <p>
            Any Order Form, Statement of Work, Statement of Engagement, proposal, invoice, or similar
            written agreement executed or accepted by both parties shall be governed by these Terms and
            Conditions unless otherwise agreed to by the parties in writing.
          </p>
          <p>
            These Terms and Conditions, together with any applicable Order Form, Statement of Work,
            Service Level Agreement, and Addendum, constitute the entire agreement between the parties
            concerning the subject matter described herein.
          </p>
          <p>
            By signing an Order Form, accepting a proposal, paying an invoice, allowing installation of
            Sensor Tech equipment, accessing Sensetech.app, or otherwise receiving the services, Client
            agrees to be bound by this Agreement.
          </p>

          <h4>1. Services</h4>
          <h5>1.1 Services Provided</h5>
          <p>
            Subject to the terms and conditions of this Agreement, Sensor Tech will use commercially
            reasonable efforts to provide Client access to Sensor Tech&rsquo;s internet-based dashboard,
            monitoring system, email alerting system, temperature monitoring devices, and related
            services described in the applicable Order Form or Statement of Work.
          </p>
          <p>The services may include, without limitation:</p>
          <ul>
            <li>Access to the Sensetech.app dashboard;</li>
            <li>Deployment of Sensor Tech-owned temperature monitoring devices;</li>
            <li>Email-based temperature and device-status alerts;</li>
            <li>Initial setup and configuration;</li>
            <li>Basic installation of monitoring devices;</li>
            <li>Ongoing subscription monitoring;</li>
            <li>Support services related to Sensor Tech equipment and dashboard access.</li>
          </ul>
          <p>
            The specific services, devices, monitored locations, pricing, subscription term, and
            installation scope shall be set forth in the applicable Order Form or Statement of Work.
          </p>

          <h5>1.2 Client Cooperation</h5>
          <p>
            Client understands and agrees that Sensor Tech&rsquo;s performance depends in part on
            Client&rsquo;s actions, cooperation, site conditions, internet connectivity, Wi-Fi
            availability, power availability, appliance condition, access to the location, and accuracy
            of information provided by Client.
          </p>
          <p>
            Client will use commercially reasonable efforts to provide Sensor Tech with all required
            information, access, permissions, Wi-Fi credentials, power access, and assistance necessary
            to perform the services in a timely manner.
          </p>
          <p>
            Any dates, timelines, service windows, installation dates, support timelines, or performance
            obligations shall be extended as reasonably necessary to account for delays caused by
            Client, third parties, site conditions, internet issues, Wi-Fi issues, power issues, or
            other circumstances outside Sensor Tech&rsquo;s reasonable control.
          </p>

          <h5>1.3 Dashboard Access</h5>
          <p>
            Client may receive access to the Sensetech.app dashboard for viewing monitored devices,
            temperature readings, device status, and related information.
          </p>
          <p>
            Client is responsible for maintaining the confidentiality of dashboard usernames, passwords,
            and account access.
          </p>
          <p>
            Client is responsible for all activity by its users and for ensuring that its users comply
            with this Agreement.
          </p>

          <h4>2. Restrictions and Responsibilities</h4>
          <h5>2.1 Restrictions</h5>
          <p>Client will not, directly or indirectly:</p>
          <p>
            a. Reverse engineer, decompile, disassemble, copy, modify, or attempt to discover the source
            code, object code, structure, design, algorithms, or underlying technology of the Sensor
            Tech platform, dashboard, software, firmware, devices, or services;
          </p>
          <p>
            b. Modify, translate, adapt, tamper with, or create derivative works based on the Sensor
            Tech platform, dashboard, software, firmware, devices, or services;
          </p>
          <p>
            c. Use the Sensor Tech system for the benefit of any third party except as expressly
            permitted by Sensor Tech;
          </p>
          <p>
            d. Use the Sensor Tech system in any manner that interferes with, disrupts, damages, or
            impairs the operation, integrity, security, or performance of the Sensor Tech system;
          </p>
          <p>
            e. Attempt to gain unauthorized access to Sensor Tech&rsquo;s systems, devices, dashboard,
            software, APIs, networks, or related infrastructure;
          </p>
          <p>
            f. Remove, relocate, alter, unplug, damage, or tamper with Sensor Tech equipment without
            Sensor Tech&rsquo;s prior written consent;
          </p>
          <p>
            g. Use the services in violation of any applicable law, regulation, food safety rule,
            privacy rule, or third-party right.
          </p>

          <h5>2.2 Client Responsibilities</h5>
          <p>Client remains solely responsible for:</p>
          <ul>
            <li>Its business operations;</li>
            <li>Food safety procedures;</li>
            <li>Employee training;</li>
            <li>Manual inspections;</li>
            <li>Regulatory compliance;</li>
            <li>Appliance maintenance;</li>
            <li>Refrigeration practices;</li>
            <li>Physical inspection of refrigerators, freezers, walk-ins, storage areas, and other monitored locations;</li>
            <li>Responding to alerts;</li>
            <li>Maintaining current alert contact information;</li>
            <li>Maintaining working internet, Wi-Fi, and power.</li>
          </ul>
          <p>
            Sensor Tech may suspend or restrict use of the services if Sensor Tech reasonably believes
            Client&rsquo;s use violates this Agreement, creates security risk, creates operational risk,
            or violates applicable law.
          </p>

          <h5>2.3 Client Equipment, Internet, Wi-Fi, and Power</h5>
          <p>
            Client is responsible for obtaining, maintaining, and paying for all equipment, services,
            and infrastructure necessary to connect to and use the Sensor Tech system, including without
            limitation:
          </p>
          <ul>
            <li>Internet service;</li>
            <li>Wi-Fi network;</li>
            <li>Router and networking equipment;</li>
            <li>Electrical power;</li>
            <li>Mobile devices and computers;</li>
            <li>Browser access;</li>
            <li>Email access;</li>
            <li>Access to monitored areas;</li>
            <li>Proper appliance operation.</li>
          </ul>
          <p>
            Client is responsible for maintaining the security and functionality of its own equipment,
            network, passwords, usernames, internet connection, Wi-Fi connection, power supply, and
            physical premises.
          </p>
          <p>
            Client agrees to promptly notify Sensor Tech if Wi-Fi credentials, network settings, router
            equipment, power availability, site access, appliance locations, alert contacts, or
            monitored conditions change.
          </p>

          <h4>3. Compensation; Billing Procedures</h4>
          <h5>3.1 Fees</h5>
          <p>
            Unless otherwise stated in the applicable Order Form, Client shall pay Sensor Tech the
            following fees:
          </p>
          <ul>
            <li>Installation/setup fee: $100 upfront;</li>
            <li>Monthly monitoring fee: $50 per month per deployed device;</li>
            <li>Additional fees: As stated in the applicable Order Form, invoice, or written agreement.</li>
          </ul>
          <p>
            The number of devices required for a Client location depends on the layout of the equipment,
            monitored areas, site conditions, refrigeration setup, Wi-Fi coverage, and Sensor
            Tech&rsquo;s reasonable determination of what is needed to provide monitoring coverage.
          </p>

          <h5>3.2 Upfront Payment</h5>
          <p>
            The upfront installation/setup fee is due immediately upon signing the Order Form, accepting
            the proposal, or approving installation, unless otherwise stated in writing.
          </p>
          <p>
            Sensor Tech is not required to install equipment, activate devices, or provide dashboard
            access until required upfront payments have been received.
          </p>

          <h5>3.3 Monthly or Annual Subscription</h5>
          <p>
            Client may subscribe on either a monthly or annual basis, as stated in the applicable Order
            Form.
          </p>
          <p>For monthly subscriptions, monthly fees are billed automatically on the first day of each month.</p>
          <p>
            For annual subscriptions, annual fees are billed in advance unless otherwise stated in the
            applicable Order Form.
          </p>
          <p>
            Sensor Tech may offer different pricing, discounts, or terms for annual subscriptions,
            multi-location customers, larger deployments, or custom service arrangements.
          </p>

          <h5>3.4 Automatic Payments</h5>
          <p>
            Client authorizes Sensor Tech, or Sensor Tech&rsquo;s third-party payment processor, to
            automatically charge Client&rsquo;s approved payment method for all recurring subscription
            fees, applicable taxes, and authorized charges.
          </p>
          <p>Monthly recurring charges will be processed on or around the first day of each month.</p>
          <p>Client is responsible for keeping payment information current.</p>
          <p>
            If automatic payment fails, Sensor Tech may retry the payment, request updated payment
            information, suspend services, or terminate services for non-payment.
          </p>

          <h5>3.5 Late or Failed Payments</h5>
          <p>
            If Client fails to pay amounts when due, Sensor Tech may suspend dashboard access, alerting,
            monitoring services, support, and/or on-site service until all outstanding amounts are paid.
          </p>
          <p>
            Late payments may bear interest at the rate of 1.5% per month, or the maximum amount allowed
            by law, whichever is lower.
          </p>
          <p>Client remains responsible for all amounts due through the effective date of termination.</p>

          <h5>3.6 Pricing Flexibility</h5>
          <p>
            Pricing, device counts, installation charges, support charges, and service scope may vary
            based on site layout, number of devices, monitored equipment, installation complexity,
            customer requirements, and any custom terms stated in the applicable Order Form.
          </p>
          <p>
            Sensor Tech may adjust pricing for future renewal terms, additional devices, additional
            locations, expanded scope, or modified services upon written notice to Client.
          </p>
          <p>
            Any pricing adjustment for an active term shall be stated in writing or reflected in a
            mutually accepted Order Form, invoice, or renewal notice.
          </p>

          <h5>3.7 Taxes</h5>
          <p>
            Client is responsible for any applicable taxes, fees, assessments, or governmental charges
            related to the services, except taxes based on Sensor Tech&rsquo;s income.
          </p>

          <h5>3.8 Billing Window</h5>
          <p>
            Sensor Tech reserves the right to issue an actionable invoice or request for payment for any
            services rendered, devices deployed, or fees accrued under this Agreement or any Order Form
            for up to twenty-four (24) months following the date the applicable Order Form is signed or
            the services are first provided, whichever is later. Client&rsquo;s obligation to pay
            validly accrued fees shall not be waived by Sensor Tech&rsquo;s delay in invoicing within
            this period.
          </p>

          <h4>4. Confidential Information</h4>
          <h5>4.1 Confidentiality</h5>
          <p>
            Each party shall keep confidential and not disclose to any third party any non-public
            information obtained from the other party that is marked confidential, reasonably understood
            to be confidential, or disclosed under circumstances indicating confidentiality.
          </p>
          <p>Confidential Information may include, without limitation:</p>
          <ul>
            <li>Business information;</li>
            <li>Pricing;</li>
            <li>Technical information;</li>
            <li>Customer lists;</li>
            <li>Site information;</li>
            <li>Login credentials;</li>
            <li>Wi-Fi network names and passwords;</li>
            <li>System designs;</li>
            <li>Device configurations;</li>
            <li>Software information;</li>
            <li>Operational data;</li>
            <li>Non-public terms of this Agreement.</li>
          </ul>
          <p>
            Neither party shall use Confidential Information except as necessary to perform or receive
            services under this Agreement.
          </p>
          <p>
            The obligations in this Section do not apply to information that: (a) is or becomes publicly
            available through no act or omission of the receiving party; (b) was rightfully known to the
            receiving party without restriction before receipt; (c) is rightfully obtained from a third
            party who is not under an obligation of confidentiality with respect to such information;
            (d) is independently developed by the receiving party without use of or reference to the
            disclosing party&rsquo;s Confidential Information; or (e) is required to be disclosed under
            court order, subpoena, or applicable law, provided that the receiving party gives the
            disclosing party reasonable prior notice where legally permitted and discloses only the
            minimum extent required.
          </p>

          <h5>4.2 Wi-Fi Credentials</h5>
          <p>
            If Client provides Sensor Tech with Wi-Fi network names, passwords, or similar access
            credentials, Sensor Tech will use such credentials solely to install, configure, maintain,
            troubleshoot, or support the services.
          </p>
          <p>
            Client remains responsible for its network security and may change credentials at any time,
            provided Client promptly notifies Sensor Tech if such change affects service operation.
          </p>

          <h4>5. Term and Termination</h4>
          <h5>5.1 Term</h5>
          <p>
            This Agreement begins on the effective date stated in the applicable Order Form or, if no
            effective date is stated, on the date Client first accepts the services, signs an Order
            Form, pays an invoice, allows installation of Sensor Tech equipment, or accesses
            Sensetech.app.
          </p>
          <p>
            This Agreement continues until all active Order Forms, subscriptions, or services expire or
            are terminated.
          </p>

          <h5>5.2 Monthly Subscription Term</h5>
          <p>
            If Client selects monthly billing, the subscription shall continue month-to-month until
            terminated by either party in accordance with this Agreement.
          </p>
          <p>
            Either party may terminate a monthly subscription upon thirty (30) days&rsquo; written
            notice unless otherwise stated in the applicable Order Form.
          </p>

          <h5>5.3 Annual Subscription Term</h5>
          <p>
            If Client selects annual billing, the subscription shall continue for the annual term stated
            in the applicable Order Form.
          </p>
          <p>
            Unless otherwise stated in the Order Form, annual subscriptions automatically renew for
            additional one-year terms unless either party gives written notice of non-renewal at least
            thirty (30) days before the end of the then-current annual term.
          </p>

          <h5>5.4 Termination for Breach</h5>
          <p>
            Either party may terminate this Agreement if the other party materially breaches the
            Agreement and does not cure such breach within thirty (30) days after receiving written
            notice of the breach.
          </p>
          <p>Sensor Tech may suspend or terminate services immediately if Client:</p>
          <ul>
            <li>Fails to pay amounts due;</li>
            <li>Misuses the services;</li>
            <li>Tampers with Sensor Tech equipment;</li>
            <li>Removes, relocates, damages, or unplugs Sensor Tech equipment without approval;</li>
            <li>Creates a safety or security risk;</li>
            <li>Violates applicable law;</li>
            <li>Uses the services in a manner that could damage Sensor Tech systems or reputation.</li>
          </ul>

          <h5>5.5 Effect of Termination</h5>
          <p>Upon termination:</p>
          <ul>
            <li>Client shall stop using the Sensor Tech dashboard and services;</li>
            <li>Client shall pay all amounts due;</li>
            <li>Each party shall return or destroy the other party&rsquo;s Confidential Information upon request;</li>
            <li>Client shall return Sensor Tech-owned equipment;</li>
            <li>Sensor Tech may remove access to dashboard, alerts, reports, and data exports.</li>
          </ul>
          <p>
            Within thirty (30) days after the effective date of termination or expiration of this
            Agreement or any Order Form, Client shall pay Sensor Tech all amounts due or accrued as of
            that date in accordance with the applicable Order Form.
          </p>
          <p>
            The following Sections shall survive any termination or expiration of this Agreement:
            Section 2 (Restrictions and Responsibilities), Section 3 (Compensation; Billing Procedures),
            Section 4 (Confidential Information), Section 6 (Warranty and Disclaimer), Section 7
            (License and Ownership Rights), Section 8 (Equipment), and Section 9 (General Terms),
            together with any other provision that by its nature should survive.
          </p>

          <h4>6. Warranty and Disclaimer</h4>
          <h5>6.1 Limited Service Warranty</h5>
          <p>
            Sensor Tech represents that it will perform services in a professional and workmanlike
            manner using personnel or contractors with reasonable skill for the services provided.
          </p>

          <h5>6.2 Monitoring Disclaimer</h5>
          <p>
            Client understands and agrees that the Sensor Tech system is a monitoring and email alerting
            tool only.
          </p>
          <p>Sensor Tech does not guarantee:</p>
          <ul>
            <li>That any refrigerator, freezer, walk-in, appliance, or monitored equipment will operate properly;</li>
            <li>That food, beverages, medicine, inventory, or other stored goods will remain safe or usable;</li>
            <li>That all temperature events will be detected;</li>
            <li>That all alerts will be delivered, received, read, or acted upon;</li>
            <li>That internet, Wi-Fi, email, third-party messaging services, or power will be uninterrupted;</li>
            <li>That sensor readings will be error-free, uninterrupted, or perfectly accurate;</li>
            <li>That the dashboard will be available at all times;</li>
            <li>That the services will prevent spoilage, loss, theft, equipment failure, regulatory violations, or business interruption.</li>
          </ul>

          <h5>6.3 Appliance Disclaimer</h5>
          <p>
            Sensor Tech does not inspect, maintain, repair, service, warrant, or control Client&rsquo;s
            refrigeration units, freezers, coolers, HVAC equipment, appliances, electrical systems,
            plumbing systems, food storage procedures, employee procedures, or physical premises.
          </p>
          <p>
            Client remains solely responsible for maintaining all appliances and monitored equipment in
            proper working order.
          </p>
          <p>
            Client remains solely responsible for food safety, regulatory compliance, physical
            inspections, manual temperature checks where required, and corrective action when an issue
            occurs.
          </p>

          <h5>6.4 No Emergency Service</h5>
          <p>
            Unless expressly stated in an Order Form, Sensor Tech does not provide emergency
            refrigeration repair, emergency appliance service, emergency food safety response, or 24/7
            on-site emergency response.
          </p>
          <p>
            Sensor Tech alerts are intended to assist Client but are not a substitute for Client&rsquo;s
            own inspection, maintenance, and operational procedures.
          </p>

          <h5>6.5 Disclaimer of Warranties</h5>
          <p>
            Except as expressly stated in this Agreement, the services, dashboard, software, sensors,
            devices, equipment, email alerts, reports, and related materials are provided &ldquo;as
            is&rdquo; and &ldquo;as available.&rdquo;
          </p>
          <p>
            Sensor Tech disclaims all warranties, express or implied, including without limitation
            warranties of merchantability, fitness for a particular purpose, non-infringement, accuracy,
            uninterrupted use, error-free operation, and suitability for food safety compliance.
          </p>

          <h4>7. License and Ownership Rights</h4>
          <h5>7.1 Client IP and Client Data</h5>
          <p>
            Client retains ownership of its own content, trademarks, business information, site
            information, and other intellectual property provided to Sensor Tech.
          </p>
          <p>
            Client grants Sensor Tech a non-exclusive, royalty-free license to use Client information,
            site information, operational information, temperature data, alert contact information, and
            related data as necessary to provide, operate, maintain, improve, troubleshoot, and support
            the services.
          </p>

          <h5>7.2 Sensor Tech IP</h5>
          <p>
            Sensor Tech retains all right, title, and interest in and to its services, Sensetech.app
            dashboard, software, firmware, hardware designs, device configurations, documentation,
            technology, know-how, trademarks, trade names, business methods, processes, analytics,
            templates, and intellectual property.
          </p>
          <p>
            No rights are granted to Client except the limited right to use the services during the term
            of the Agreement.
          </p>

          <h5>7.3 Feedback</h5>
          <p>
            If Client provides suggestions, ideas, improvements, feature requests, comments, or other
            feedback regarding the services, Client grants Sensor Tech a non-exclusive, royalty-free,
            perpetual, irrevocable, transferable, sublicensable license to use, modify, commercialize,
            and incorporate such feedback without restriction or compensation.
          </p>

          <h4>8. Equipment</h4>
          <h5>8.1 Sensor Tech-Owned Equipment</h5>
          <p>
            Unless otherwise stated in the applicable Order Form, all monitoring devices, sensors,
            probes, gateways, enclosures, power supplies, and related equipment deployed by Sensor Tech
            remain the sole property of Sensor Tech.
          </p>
          <p>Client receives no ownership rights in Sensor Tech equipment.</p>
          <p>
            Client may use Sensor Tech equipment only during the active subscription term and only for
            the purpose of receiving Sensor Tech services.
          </p>

          <h5>8.2 Client Responsibilities for Equipment</h5>
          <p>Client agrees that it will not:</p>
          <ul>
            <li>Sell, rent, assign, transfer, or dispose of Sensor Tech equipment;</li>
            <li>Modify, open, repair, alter, or tamper with Sensor Tech equipment;</li>
            <li>Move Sensor Tech equipment without Sensor Tech&rsquo;s prior approval;</li>
            <li>Disconnect or unplug Sensor Tech equipment except in an emergency;</li>
            <li>Attach additional devices, accessories, cables, or parts to Sensor Tech equipment without approval;</li>
            <li>Damage, misuse, neglect, or expose Sensor Tech equipment to unreasonable conditions.</li>
          </ul>
          <p>
            Client agrees to use commercially reasonable efforts to protect Sensor Tech equipment from
            theft, damage, vandalism, water intrusion, negligent handling, and unauthorized access.
          </p>

          <h5>8.3 Risk of Loss</h5>
          <p>
            From the date Sensor Tech equipment is delivered or installed at Client&rsquo;s location,
            Client assumes responsibility for loss, theft, damage, destruction, or impairment of Sensor
            Tech equipment except to the extent caused solely by Sensor Tech.
          </p>
          <p>
            If equipment is lost, stolen, damaged, destroyed, tampered with, moved, or impaired due to
            Client, Client employees, Client contractors, customers, site conditions, water exposure,
            misuse, relocation, power issues, or other non-Sensor Tech causes, Client shall pay Sensor
            Tech for repair, replacement, shipping, and re-installation costs.
          </p>

          <h5>8.4 Return of Equipment</h5>
          <p>
            Upon termination or expiration of services, Client shall return all Sensor Tech-owned
            equipment within ten (10) days unless otherwise agreed in writing.
          </p>
          <p>
            Client shall provide reasonable access for Sensor Tech to remove equipment if Sensor Tech
            elects to remove equipment on-site.
          </p>
          <p>
            Client is responsible for return shipping, packaging, access for removal, and any costs
            associated with unreturned, damaged, or missing equipment.
          </p>
          <p>
            If Client fails to return Sensor Tech-owned equipment, Sensor Tech may charge Client the
            replacement cost of the equipment plus reasonable recovery, shipping, and administrative
            costs.
          </p>

          <h5>8.5 Recovery of Equipment</h5>
          <p>
            Upon (i) termination of this Agreement or any Order Form by Sensor Tech for Client&rsquo;s
            breach, or (ii) Client&rsquo;s relocation or removal of Sensor Tech equipment from its
            installed Location without Sensor Tech&rsquo;s prior written approval, Sensor Tech may,
            following reasonable written notice and subject to applicable law, enter the Location during
            normal business hours to inspect, disable, and recover its equipment.
          </p>
          <p>
            Client grants Sensor Tech and its authorized agents a limited license to enter premises
            under Client&rsquo;s control or jurisdiction for the sole purpose of recovering Sensor Tech
            equipment, provided that Sensor Tech shall conduct any such recovery without breach of the
            peace and shall repair any physical damage to the premises caused directly by the removal.
          </p>
          <p>
            Client agrees to reasonably cooperate with any such recovery, including by making the
            equipment accessible and assisting with assembly or disassembly upon request. This remedy is
            in addition to, and not in lieu of, Sensor Tech&rsquo;s right to charge replacement,
            recovery, shipping, and administrative costs under Sections 8.3 and 8.4.
          </p>

          <h4>9. General Terms</h4>
          <h5>9.1 Publicity</h5>
          <p>
            Neither party shall issue a press release or public statement regarding the relationship
            between the parties without the prior written consent of the other party.
          </p>
          <p>
            Sensor Tech may not use Client&rsquo;s name, logo, or site information in marketing
            materials without Client&rsquo;s prior written consent.
          </p>

          <h5>9.2 Independent Contractor</h5>
          <p>The parties are independent contractors.</p>
          <p>
            Nothing in this Agreement creates a partnership, joint venture, agency, employment
            relationship, fiduciary relationship, franchise, or other similar relationship between the
            parties.
          </p>
          <p>Neither party may bind the other party to any obligation.</p>

          <h5>9.3 Excusable Delay</h5>
          <p>
            Neither party shall be liable for delay or failure to perform caused by events beyond its
            reasonable control, including without limitation acts of God, natural disasters, fires,
            floods, power outages, internet outages, utility failures, labor disputes, supply chain
            delays, governmental actions, war, terrorism, pandemics, network failures, third-party
            service failures, or other events beyond the party&rsquo;s reasonable control.
          </p>
          <p>Failure to make payment is not excused by this section.</p>

          <h5>9.4 Governing Law; Venue</h5>
          <p>
            This Agreement shall be governed by the laws of the State of California, without regard to
            conflict of law principles.
          </p>
          <p>
            Venue for any dispute shall be in the state or federal courts located in Placer County,
            California, unless otherwise required by law.
          </p>

          <h5>9.5 Limitation of Liability</h5>
          <p>
            To the maximum extent permitted by law, Sensor Tech shall not be liable under any contract,
            tort, negligence, strict liability, or other legal or equitable theory for:
          </p>
          <p>a. Spoiled food, beverages, medicine, inventory, or other products;</p>
          <p>b. Lost profits, lost revenue, loss of business, loss of goodwill, loss of use, or business interruption;</p>
          <p>c. Regulatory fines, failed inspections, or compliance failures;</p>
          <p>d. Failure of refrigeration, freezer, HVAC, electrical, internet, Wi-Fi, router, power, or appliance systems;</p>
          <p>e. Missed, delayed, failed, inaccurate, or undelivered alerts;</p>
          <p>f. Loss, corruption, or inaccuracy of data;</p>
          <p>g. Substitute goods, substitute services, emergency repair, or replacement inventory;</p>
          <p>h. Indirect, incidental, special, consequential, exemplary, punitive, or enhanced damages.</p>
          <p>
            Sensor Tech&rsquo;s total aggregate liability for all claims arising out of or related to
            this Agreement shall not exceed the amounts paid by Client to Sensor Tech under the
            applicable Order Form during the twelve (12) months immediately preceding the event giving
            rise to the claim.
          </p>
          <p>
            If Client has paid less than twelve (12) months of fees, Sensor Tech&rsquo;s total liability
            shall not exceed the amount actually paid by Client to Sensor Tech.
          </p>

          <h5>9.6 Indemnification</h5>
          <p>
            Client agrees to defend, indemnify, and hold harmless Sensor Tech and its owners, partners,
            employees, contractors, agents, successors, and assigns from and against any claims,
            damages, losses, liabilities, settlements, penalties, costs, and expenses, including
            attorneys&rsquo; fees, arising out of or related to:
          </p>
          <ul>
            <li>Client&rsquo;s use of the services;</li>
            <li>Client&rsquo;s appliances, refrigeration equipment, HVAC equipment, electrical systems, network, premises, food storage, or business operations;</li>
            <li>Client&rsquo;s failure to maintain equipment or appliances;</li>
            <li>Client&rsquo;s failure to respond to alerts;</li>
            <li>Client&rsquo;s failure to maintain current alert contact information;</li>
            <li>Client&rsquo;s violation of applicable laws or regulations;</li>
            <li>Client&rsquo;s breach of this Agreement;</li>
            <li>Client&rsquo;s negligence, willful misconduct, or misuse of the services;</li>
            <li>Claims related to spoiled goods, food safety, inventory loss, or business interruption.</li>
          </ul>

          <h5>9.7 Assignment</h5>
          <p>Client may not assign this Agreement without Sensor Tech&rsquo;s prior written consent.</p>
          <p>
            Sensor Tech may assign this Agreement in connection with a merger, acquisition, sale of
            assets, corporate reorganization, change in ownership, or transfer of substantially all of
            its relevant business.
          </p>

          <h5>9.8 Notices</h5>
          <p>
            All notices under this Agreement must be in writing and sent by email, certified mail,
            overnight courier, or other method stated in the applicable Order Form.
          </p>
          <p>Notices to Sensor Tech shall be sent to:</p>
          <p>Sensor Tech Address: __________________________ Email: ____________________________</p>
          <p>Notices to Client shall be sent to the contact information listed in the applicable Order Form.</p>

          <h5>9.9 Order of Precedence</h5>
          <p>
            If there is a conflict between these Terms and Conditions and an Order Form, the Terms and
            Conditions control unless the Order Form expressly states that it overrides a specific
            section of these Terms and Conditions.
          </p>

          <h5>9.10 Severability</h5>
          <p>
            If any provision of this Agreement is found invalid or unenforceable, the provision shall be
            limited or modified to the minimum extent necessary so that the rest of the Agreement
            remains enforceable.
          </p>

          <h5>9.11 Waiver</h5>
          <p>
            No waiver, change, or modification of this Agreement is effective unless in writing and
            signed by both parties.
          </p>
          <p>Failure to enforce any provision shall not constitute a waiver of that provision.</p>

          <h5>9.12 Electronic Signatures</h5>
          <p>
            This Agreement and related documents may be signed electronically and in counterparts.
            Electronic signatures shall have the same legal effect as original signatures.
          </p>

          <hr />

          <h3>SENSOR TECH SERVICE LEVEL AGREEMENT</h3>

          <h5>1. Subscription Services Availability</h5>
          <p>
            Sensor Tech will use commercially reasonable efforts to make Sensetech.app and the
            subscription services available 24 hours per day, 7 days per week, excluding scheduled
            maintenance, unscheduled maintenance, third-party outages, Client network issues, Client
            Wi-Fi issues, Client power issues, device failures, internet outages, and other
            circumstances outside Sensor Tech&rsquo;s reasonable control.
          </p>

          <h5>2. Scheduled Maintenance</h5>
          <p>Sensor Tech may perform scheduled maintenance during low-usage periods.</p>
          <p>
            Sensor Tech will use commercially reasonable efforts to provide advance notice of scheduled
            maintenance that is expected to materially affect dashboard availability or email alert
            delivery.
          </p>

          <h5>3. Unscheduled Maintenance</h5>
          <p>
            Unscheduled maintenance may be required to address security issues, software issues, device
            issues, bugs, outages, or other problems affecting the services.
          </p>
          <p>
            Sensor Tech will use commercially reasonable efforts to notify Client of unscheduled
            maintenance when practical.
          </p>

          <h5>4. Scope of Support</h5>
          <p>
            Support is limited to the Sensetech.app dashboard, Sensor Tech monitoring devices, Sensor
            Tech email alert configuration, and Sensor Tech-installed equipment.
          </p>
          <p>
            Sensor Tech is not responsible for maintenance, repair, replacement, troubleshooting, or
            service of Client&rsquo;s refrigerators, freezers, walk-ins, appliances, compressors, HVAC
            equipment, electrical systems, routers, internet service, Wi-Fi network, or third-party
            systems.
          </p>

          <h5>5. Client Verification Before On-Site Support</h5>
          <p>
            Before requesting on-site support, Client must use commercially reasonable efforts to verify
            and provide evidence that:
          </p>
          <ul>
            <li>Power is available at the device location;</li>
            <li>The Wi-Fi network is operational;</li>
            <li>Other devices can connect to the Wi-Fi network;</li>
            <li>Internet access is available;</li>
            <li>The device has not been unplugged, moved, damaged, blocked, or tampered with;</li>
            <li>The refrigerator/freezer/appliance itself is operating or has been separately evaluated as needed;</li>
            <li>Any known site changes have been disclosed to Sensor Tech.</li>
          </ul>
          <p>
            If Sensor Tech provides on-site support and the issue is caused by Client&rsquo;s Wi-Fi,
            internet, power, appliance, site conditions, tampering, relocation, or other
            non-Sensor Tech cause, Sensor Tech may charge Client for the visit at the rate stated in the
            applicable Order Form.
          </p>

          <h5>6. Support Response</h5>
          <p>
            Sensor Tech will use commercially reasonable efforts to provide an initial response to
            reported service issues within two (2) business days.
          </p>
          <p>
            A &ldquo;response&rdquo; means that Sensor Tech acknowledges the issue and begins reasonable
            troubleshooting, investigation, or support communication.
          </p>
          <p>A response does not guarantee immediate resolution.</p>

          <h5>7. On-Site Visits</h5>
          <p>
            If Sensor Tech determines that an on-site visit is necessary and the issue is within Sensor
            Tech&rsquo;s support scope, Sensor Tech will use commercially reasonable efforts to perform
            the on-site visit within two (2) weeks after:
          </p>
          <ul>
            <li>Sensor Tech determines that an on-site visit is necessary;</li>
            <li>Client completes required verification;</li>
            <li>Client provides site access;</li>
            <li>Client confirms that Wi-Fi, internet, and power are operational;</li>
            <li>Required replacement equipment, if any, is available.</li>
          </ul>
          <p>
            On-site visit timelines may be extended due to scheduling, hardware availability, shipping
            delays, Client access issues, unsafe site conditions, force majeure events, or circumstances
            outside Sensor Tech&rsquo;s reasonable control.
          </p>

          <h5>8. Support Tiers</h5>
          <p>Sensor Tech will use commercially reasonable efforts to provide support under the following categories:</p>
          <p><strong>Tier 1: General Questions and Login Issues</strong></p>
          <p>Examples:</p>
          <ul>
            <li>Dashboard login help;</li>
            <li>User account questions;</li>
            <li>Basic dashboard use questions;</li>
            <li>Email alert recipient updates;</li>
            <li>Basic configuration questions.</li>
          </ul>
          <p>Target first response: two (2) business days.</p>
          <p><strong>Tier 2: Software, Dashboard, Email Alerting, or Configuration Issues</strong></p>
          <p>Examples:</p>
          <ul>
            <li>Dashboard bugs;</li>
            <li>Email alert configuration issues;</li>
            <li>Reporting issues;</li>
            <li>Device configuration updates;</li>
            <li>Software troubleshooting.</li>
          </ul>
          <p>Target first response: two (2) business days.</p>
          <p><strong>Tier 3: Device Offline, Hardware Damage, or Sensor Replacement</strong></p>
          <p>Examples:</p>
          <ul>
            <li>Sensor offline;</li>
            <li>Gateway offline;</li>
            <li>Physical device damage;</li>
            <li>Sensor probe failure;</li>
            <li>Hardware replacement;</li>
            <li>On-site technician assistance if required.</li>
          </ul>
          <p>Target first response: two (2) business days.</p>
          <p>
            Target on-site visit, if required and included: within two (2) weeks after Sensor Tech
            determines that on-site service is necessary and Client has completed required verification.
          </p>
          <p><strong>Tier 4: Feature Requests</strong></p>
          <p>Examples:</p>
          <ul>
            <li>New dashboard features;</li>
            <li>Custom reports;</li>
            <li>Custom integrations;</li>
            <li>Product enhancements.</li>
          </ul>
          <p>No guaranteed timeline unless separately agreed in writing.</p>

          <h5>9. Device Failure and Replacement</h5>
          <p>
            If Sensor Tech determines that Sensor Tech-provided equipment has failed under normal use,
            Sensor Tech will use commercially reasonable efforts to repair, replace, or reconfigure the
            equipment.
          </p>
          <p>
            Replacement timelines may depend on hardware availability, shipping delays, site access,
            Client cooperation, and other factors outside Sensor Tech&rsquo;s reasonable control.
          </p>
          <p>
            If the equipment failed due to Client misuse, damage, water intrusion, tampering,
            relocation, loss, theft, improper power, improper handling, or site conditions outside
            normal use, Client may be responsible for repair, replacement, shipping, and installation
            costs.
          </p>

          <h5>10. Service Credits</h5>
          <p>
            If Sensor Tech fails to provide services in accordance with a specific service commitment
            expressly stated in an Order Form, Client&rsquo;s sole remedy shall be a service credit
            equal to the subscription fees paid for the affected device during the period in which the
            services were not functioning properly due solely to Sensor Tech&rsquo;s failure.
          </p>
          <p>Service credits shall not apply to outages or failures caused by:</p>
          <ul>
            <li>Client internet, Wi-Fi, router, or network issues;</li>
            <li>Power outages;</li>
            <li>Appliance failure;</li>
            <li>Client tampering, movement, or damage to equipment;</li>
            <li>Incorrect email alert contact information;</li>
            <li>Failure to check email;</li>
            <li>Failure to respond to alerts;</li>
            <li>Third-party email, internet, or hosting failures;</li>
            <li>Force majeure events;</li>
            <li>Scheduled or emergency maintenance;</li>
            <li>Client breach of this Agreement.</li>
          </ul>
          <p>Service credits must be requested in writing within ten (10) days after the issue occurred.</p>
          <p>Service credits are Client&rsquo;s sole and exclusive remedy for service availability issues.</p>

          <hr />

          <h3>SENSOR TECH TEMPERATURE MONITORING SERVICES ADDENDUM</h3>
          <p>
            This Sensor Tech Temperature Monitoring Services Addendum describes the terms under which
            Sensor Tech provides Client access to the Sensor Tech temperature monitoring system,
            Sensetech.app dashboard, email alerting tools, and related services.
          </p>
          <p>
            Unless otherwise stated, this Addendum is in addition to and does not replace the Sensor
            Tech Terms and Conditions of Service.
          </p>
          <p>
            In the event of conflict between this Addendum and the Terms and Conditions, this Addendum
            controls solely with respect to temperature monitoring services.
          </p>

          <h5>1. Definitions</h5>
          <p>
            &ldquo;Monitoring System&rdquo; means Sensor Tech&rsquo;s dashboard, software, hardware,
            sensors, devices, probes, email alerting tools, configurations, and related services used to
            monitor temperature or related conditions.
          </p>
          <p>
            &ldquo;Location&rdquo; means the site, building, room, refrigerator, freezer, walk-in,
            storage area, or other area identified in the applicable Order Form.
          </p>
          <p>
            &ldquo;Device&rdquo; means any Sensor Tech monitoring device, gateway, sensor, probe,
            enclosure, cable, power supply, or related equipment.
          </p>
          <p>
            &ldquo;Appliance&rdquo; means any refrigerator, freezer, walk-in cooler, walk-in freezer,
            HVAC unit, storage unit, or other Client equipment being monitored.
          </p>
          <p>
            &ldquo;Client Data&rdquo; means data submitted, collected, transmitted, or generated through
            the Monitoring System, including temperature readings, timestamps, alerts, device status,
            location information, user information, and related operational information.
          </p>

          <h5>2. Monitoring Services</h5>
          <p>
            Sensor Tech will use commercially reasonable efforts to provide Client access to the
            Monitoring System for the locations and devices identified in the applicable Order Form.
          </p>
          <p>
            The Monitoring System may collect temperature readings and device status information and may
            generate email alerts based on thresholds, device status, or other configured conditions.
          </p>
          <p>
            Client understands that the Monitoring System is intended to assist with visibility and
            notification. It is not a replacement for physical inspections, manual logs where required,
            appliance maintenance, food safety procedures, employee training, emergency response
            procedures, or regulatory compliance.
          </p>

          <h5>3. Client Site Information</h5>
          <p>
            Before installation or activation, Client shall provide Sensor Tech with all information
            reasonably necessary to configure the Monitoring System, including:
          </p>
          <ul>
            <li>Location name and address;</li>
            <li>Appliance type;</li>
            <li>Monitored area name;</li>
            <li>Desired sensor placement;</li>
            <li>Temperature thresholds;</li>
            <li>Email alert recipients;</li>
            <li>Email addresses;</li>
            <li>Wi-Fi network name;</li>
            <li>Wi-Fi password;</li>
            <li>Power availability;</li>
            <li>Site access instructions;</li>
            <li>Business hours;</li>
            <li>Emergency contact information;</li>
            <li>Any known site limitations.</li>
          </ul>
          <p>Client is responsible for ensuring this information is accurate and current.</p>

          <h5>4. Device Count and Site Layout</h5>
          <p>
            Client understands that the number of devices required depends on the physical layout of the
            equipment, the number of appliances or monitored areas, Wi-Fi coverage, sensor placement
            limitations, walls, distance, power availability, and other site conditions.
          </p>
          <p>
            Sensor Tech will determine the recommended number of deployed devices based on the site
            layout and monitoring needs.
          </p>
          <p>
            Client will be charged the monthly per-device fee for each deployed device unless otherwise
            stated in the applicable Order Form.
          </p>

          <h5>5. Installation</h5>
          <p>
            If installation is included in the applicable Order Form, Sensor Tech or its designee will
            install the devices at the Location.
          </p>
          <p>Client will:</p>
          <ul>
            <li>Provide reasonable access to the Location;</li>
            <li>Provide access to monitored appliances or areas;</li>
            <li>Provide access to Wi-Fi and power;</li>
            <li>Cooperate with Sensor Tech during installation;</li>
            <li>Ensure the site is safe and accessible;</li>
            <li>Obtain any required landlord, property manager, or third-party approvals;</li>
            <li>Identify any hazards, restrictions, or special requirements.</li>
          </ul>
          <p>
            Unless otherwise stated in the Order Form, installation includes basic installation only and
            does not include electrical work, refrigeration work, appliance repair, network
            installation, router configuration, cabling through walls, construction, permitting, lift
            rental, or after-hours labor.
          </p>
          <p>
            Client acknowledges and agrees that installation of Devices typically requires drilling,
            boring, or otherwise creating one or more holes or penetrations in the wall, door, gasket,
            panel, or housing of the Appliance to route sensor probes or cabling. Client authorizes
            Sensor Tech to perform such drilling and penetration as reasonably necessary to install the
            Devices. Client represents and warrants that it has the authority to authorize such
            modifications, including any required consent from the Appliance owner, landlord, or
            property manager, and that obtaining any such required consent is Client&rsquo;s
            responsibility.
          </p>
          <p>
            Upon removal of any Device, Client is responsible for patching, sealing, or repairing any
            penetration point created during installation. Sensor Tech is not obligated to restore any
            Appliance to its pre-installation condition.
          </p>

          <h5>6. Appliance Responsibility</h5>
          <p>
            Client acknowledges that Sensor Tech does not own, operate, control, maintain, repair, or
            warrant Client&rsquo;s Appliances.
          </p>
          <p>
            Client is solely responsible for ensuring that all Appliances are maintained in ordinary
            working order.
          </p>
          <p>
            Client agrees that Sensor Tech is not liable for Appliance failure, temperature excursions
            caused by Appliance failure, food spoilage, product loss, business interruption, regulatory
            issues, or losses caused by Client&rsquo;s failure to inspect, maintain, or repair
            Appliances.
          </p>
          <p>
            Client further acknowledges that drilling or penetrating an Appliance to install Devices may
            void or limit the Appliance manufacturer&rsquo;s warranty, affect the Appliance&rsquo;s
            seal, insulation, or efficiency, or require repair upon Device removal. Client accepts these
            risks and agrees that Sensor Tech is not liable for any reduction in Appliance performance,
            loss of manufacturer warranty, condensation, seal degradation, or efficiency loss arising
            from properly performed installation. Sensor Tech remains responsible only for damage caused
            by its own gross negligence or willful misconduct in performing the installation.
          </p>

          <h5>7. Email Alerts</h5>
          <p>Sensor Tech may provide alerts by email.</p>
          <p>
            Client understands and agrees that email alerts may be delayed, blocked, filtered,
            undelivered, missed, misconfigured, or unavailable due to factors including:
          </p>
          <ul>
            <li>Internet outage;</li>
            <li>Wi-Fi failure;</li>
            <li>Power loss;</li>
            <li>Device failure;</li>
            <li>Email provider issues;</li>
            <li>Spam filtering;</li>
            <li>Incorrect email address;</li>
            <li>User email settings;</li>
            <li>Dashboard maintenance;</li>
            <li>Third-party service outages;</li>
            <li>Client network changes;</li>
            <li>Client failure to respond.</li>
          </ul>
          <p>
            Client is responsible for keeping email alert contact information current and for promptly
            responding to alerts.
          </p>
          <p>
            Client is solely responsible for setting up, configuring, and maintaining alert rules, alert
            conditions, alert recipients, recipient email addresses, and notification settings within
            the dashboard or as otherwise provided to Sensor Tech. Sensor Tech is not responsible for
            alerts that are missed, misdirected, undelivered, or ineffective as a result of
            Client&rsquo;s configuration, choice of recipients, inaccurate or outdated recipient
            information, or failure to test the alert setup. Client is responsible for verifying that
            configured alerts are functioning as intended.
          </p>
          <p>
            Sensor Tech does not guarantee that any email alert will be delivered, received, read, or
            acted upon.
          </p>

          <h5>8. Thresholds and Alarm Configuration</h5>
          <p>
            Client is solely responsible for setting up and configuring all temperature thresholds,
            alarm thresholds, alarm conditions, trigger settings, and related monitoring parameters for
            its Appliances and monitored locations, whether configured by Client within the dashboard or
            provided to Sensor Tech for entry.
          </p>
          <p>
            Client is solely responsible for determining appropriate thresholds and alarm settings for
            its business, products, inventory, appliances, and regulatory requirements. Sensor Tech does
            not set, recommend, or validate appropriate threshold or alarm values and does not provide
            food safety, regulatory, refrigeration engineering, or legal advice.
          </p>
          <p>
            To the extent Sensor Tech enters threshold or alarm settings at Client&rsquo;s direction, it
            does so solely as an administrative convenience based on values supplied by Client, and
            Client remains solely responsible for the accuracy and appropriateness of those values.
            Sensor Tech is not liable for any temperature excursion, spoilage, loss, or other harm
            resulting from thresholds or alarm settings that are incorrect, inappropriate, missing,
            disabled, or misconfigured.
          </p>

          <h5>9. Data</h5>
          <p>
            Sensor Tech may collect, store, display, transmit, and process Client Data to provide and
            improve the Monitoring System and related services.
          </p>
          <p>Client grants Sensor Tech a non-exclusive, worldwide, royalty-free license to use Client Data for:</p>
          <ul>
            <li>Providing the services;</li>
            <li>Displaying dashboard information;</li>
            <li>Sending email alerts;</li>
            <li>Troubleshooting;</li>
            <li>Support;</li>
            <li>Device health monitoring;</li>
            <li>Internal analytics;</li>
            <li>Product improvement;</li>
            <li>Security and reliability.</li>
          </ul>
          <p>
            Sensor Tech may use aggregated or de-identified data for analytics, benchmarking, product
            development, and business purposes, provided such data does not identify Client.
          </p>
          <p>
            Sensor Tech will use commercially reasonable efforts to protect Client Data but does not
            guarantee that data will never be lost, corrupted, accessed, or unavailable.
          </p>

          <h5>10. Equipment Ownership</h5>
          <p>All Devices are owned by Sensor Tech unless otherwise stated in the applicable Order Form.</p>
          <p>Client agrees that:</p>
          <ul>
            <li>Devices remain Sensor Tech property;</li>
            <li>Client receives no ownership rights;</li>
            <li>Client may not sell, rent, assign, transfer, modify, tamper with, or remove Devices;</li>
            <li>Client must protect Devices from damage, theft, misuse, water intrusion, vandalism, or negligent handling;</li>
            <li>Client must notify Sensor Tech promptly of any loss, damage, theft, malfunction, relocation, or tampering;</li>
            <li>Client must return Devices after termination unless otherwise agreed.</li>
          </ul>

          <h5>11. Risk of Loss and Damage</h5>
          <p>
            From the date Devices are delivered or installed, Client is responsible for loss, theft,
            damage, destruction, misuse, or impairment of Devices while located at Client&rsquo;s site,
            except to the extent caused solely by Sensor Tech.
          </p>
          <p>
            If a Device is lost, stolen, damaged, destroyed, tampered with, or impaired due to Client,
            Client employees, Client contractors, customers, site conditions, water exposure, misuse,
            relocation, power issues, or other non-Sensor Tech causes, Client shall pay Sensor Tech for
            repair, replacement, shipping, and re-installation costs.
          </p>

          <h5>12. Return and Recovery of Equipment</h5>
          <p>
            Client shall return all Sensor Tech-owned Devices within ten (10) days after termination or
            expiration of the applicable Order Form, unless otherwise agreed in writing.
          </p>
          <p>
            Client is responsible for return shipping, packaging, access for removal, and any costs
            associated with unreturned, damaged, or missing Devices.
          </p>
          <p>
            If Client fails to return Sensor Tech-owned Devices, Sensor Tech may charge Client the
            replacement cost of the Devices plus reasonable recovery, shipping, and administrative
            costs.
          </p>
          <p>
            The recovery rights set forth in Section 8.5 of the Terms and Conditions apply to Devices
            under this Addendum.
          </p>

          <h5>13. No Remote Control or Optimization</h5>
          <p>
            Unless expressly stated in the applicable Order Form, Sensor Tech does not provide remote
            equipment control, refrigeration control, HVAC control, energy optimization, demand
            response, or appliance automation.
          </p>
          <p>
            Sensor Tech&rsquo;s services are limited to monitoring, dashboard access, email alerts, and
            related support.
          </p>

          <h5>14. No Food Safety Guarantee</h5>
          <p>
            Client acknowledges that Sensor Tech does not guarantee food safety, product safety,
            regulatory compliance, or prevention of spoilage.
          </p>
          <p>
            Client remains solely responsible for all food safety plans, regulatory logs, employee
            procedures, manual inspections, corrective actions, and compliance requirements.
          </p>
        </div>

        <div className="tos-footer">
          <button className="tos-ok" onClick={onClose}>Close</button>
        </div>

        <style>{`
          .tos-overlay {
            position: fixed;
            inset: 0;
            background: rgba(0, 0, 0, 0.7);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 1000;
            padding: 20px;
          }
          .tos-modal {
            background: #16213e;
            border: 1px solid #3a3a5a;
            border-radius: 12px;
            max-width: 760px;
            width: 100%;
            max-height: 85vh;
            display: flex;
            flex-direction: column;
            color: #ddd;
          }
          .tos-header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 16px 24px;
            border-bottom: 1px solid #3a3a5a;
          }
          .tos-header h2 {
            margin: 0;
            color: #fff;
            font-size: 20px;
          }
          .tos-close {
            background: none;
            border: none;
            color: #8fa3c8;
            font-size: 18px;
            cursor: pointer;
          }
          .tos-body {
            padding: 8px 24px 16px;
            overflow-y: auto;
            font-size: 14px;
            line-height: 1.6;
          }
          .tos-updated {
            color: #8fa3c8;
            font-size: 12px;
          }
          .tos-body h3 {
            color: #fff;
            margin: 20px 0 8px;
            font-size: 16px;
            letter-spacing: 0.5px;
          }
          .tos-body h4 {
            color: #fff;
            margin: 18px 0 6px;
            font-size: 15px;
          }
          .tos-body h5 {
            color: #c8d6f0;
            margin: 14px 0 4px;
            font-size: 14px;
          }
          .tos-body p {
            margin: 0 0 8px;
          }
          .tos-body ul {
            margin: 0 0 8px;
            padding-left: 22px;
          }
          .tos-body li {
            margin-bottom: 2px;
          }
          .tos-body hr {
            border: none;
            border-top: 1px solid #3a3a5a;
            margin: 24px 0;
          }
          .tos-body a {
            color: #4a90d9;
          }
          .tos-footer {
            padding: 12px 24px;
            border-top: 1px solid #3a3a5a;
            text-align: right;
          }
          .tos-ok {
            background: #0f3460;
            color: #fff;
            border: none;
            border-radius: 6px;
            padding: 8px 20px;
            cursor: pointer;
            font-weight: 600;
          }
          .tos-ok:hover {
            background: #1a4a80;
          }
        `}</style>
      </div>
    </div>
  );
}

export default TermsOfService;
